import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/seo/Seo";
import { getFeatureIcon } from "@/data/featureIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FeatureCard({ feature, i }) {
  const Icon = getFeatureIcon(feature.icon);
  const clickable = !!feature.has_detail_page;

  const inner = (
    <>
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 shrink-0 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center group-hover:bg-sky-600 group-hover:text-white transition-colors">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <h3 className="text-sm font-semibold text-slate-900 truncate">{feature.name}</h3>
            {clickable && (
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-sky-600 transition-colors" />
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{feature.short_desc}</p>
        </div>
      </div>
    </>
  );

  const commonClass =
    "group bg-white border border-slate-200 rounded-xl p-5 transition-all duration-200 " +
    (clickable
      ? "hover:border-sky-300 hover:shadow-md cursor-pointer"
      : "hover:border-slate-300");

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.02, duration: 0.3 }}
      data-testid={`feature-card-${feature.slug}`}
      className={commonClass}
    >
      {clickable ? (
        <Link to={`/features/${feature.slug}`} className="block">{inner}</Link>
      ) : (
        inner
      )}
    </motion.div>
  );
}

export default function FeaturesPage() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/features`);
        if (!alive) return;
        // Only show groups that actually have visible features.
        setGroups((data.groups || []).filter((g) => (g.features || []).length > 0));
        setTotal(data.total || 0);
      } catch {
        if (alive) {
          setGroups([]);
          setTotal(0);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div data-testid="features-page" className="page-transition">
      <Seo
        title="Features - Antera Software ERP & eCommerce"
        description="Purpose-built features for promotional product distributors, suppliers, decorators, print shops and sign companies - from CRM and quoting to AI reporting and automations."
        keywords="antera features, promo erp features, advance features, aether features, crm, quoting, inventory, ai reporting"
        path="/features"
      />

      {/* Hero */}
      <section className="pt-28 lg:pt-36 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-[15%] w-72 h-72 bg-sky-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-[10%] w-96 h-96 bg-sky-400/8 rounded-full blur-[120px]" />
          <svg className="absolute -bottom-[1px] left-0 w-full block" viewBox="0 0 1440 60" fill="none" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 61V30C240 0 480 0 720 30C960 60 1200 60 1440 30V61H0Z" fill="white" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative pb-16 lg:pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="text-xs font-semibold text-sky-400 tracking-widest uppercase">Antera Platform</span>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white max-w-3xl">
              Every feature you need to run a promotional products, print, or signage business.
            </h1>
            <p className="mt-3 text-base text-slate-400 max-w-2xl">
              {total || "-"} purpose-built capabilities - connected across one platform so quoting, production, fulfillment, finance, and reporting stay in sync.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="bg-sky-500 hover:bg-sky-400 text-white rounded-full h-11 px-7 text-sm font-semibold">
                  Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white rounded-full h-11 px-6 text-sm font-semibold">
                  See the products
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Groups */}
      <section className="py-14 lg:py-20 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-10" data-testid="features-loading">Loading features…</p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10" data-testid="features-empty">
              No features to show yet.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.slug} data-testid={`feature-group-${group.slug}`}>
                <div className="mb-6 max-w-3xl">
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                    {group.title}
                    <span className="ml-3 text-sm font-mono text-slate-400">{group.features.length}</span>
                  </h2>
                  {group.description && (
                    <p className="text-sm text-slate-500 mt-2">{group.description}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {group.features.map((f, i) => (
                    <FeatureCard key={f.slug} feature={f} i={i} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-sky-600 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">See it in your business.</h2>
          <p className="mt-3 text-sm text-sky-100">
            A 30-minute demo tailored to your workflow. No canned presentation-just your workflows, your suppliers, and your business.
          </p>
          <Link to="/contact" className="inline-block mt-6">
            <Button className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold">
              Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
